import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, delay, map, takeUntil, tap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { Api } from 'src/app/shared/constants/api';
import { DataService } from 'src/app/shared/services/data.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ExternalEntityService } from '../external-entity.service';

@Component({
    selector: 'app-credit-terms',
    templateUrl: './credit-terms.component.html',
    styleUrls: ['./credit-terms.component.less'],
    standalone: false
})
export class CreditTermsComponent implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy {

  @Input() disableSubmit: boolean = false;
  @Input() currencyName: string = '';

  constructor(private service: DataService, private logger: NGXLogger,
              private utilityService: UtilityService, private changeDetectorRef: ChangeDetectorRef,
              private entityService: ExternalEntityService) { }

  public creditTermForm: FormGroup = this.entityService.getCreditDetailForm();
  statusTypesList = this.utilityService.getStatusTypes();
  entityLevelList: any;
  showCustomerFields: boolean = false;
  showVendorFields: boolean = false;
  customerCategoryList: string[] = ['Operational Customer', 'General Customer'];
  vendorCategoryList: string[] = ['Operational Vendor', 'General Vendor'];

  currencyList: any[] = [];
  currencySearchCtrl = new FormControl('');
  filteredCurrency: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  searching = false;
  private onDestroy = new Subject<void>();

  ngOnInit(): void {
    this.getEntityLevelList();
    this.setCustomerFlagChangeEvent();
    this.setVendorFlagChangeEvent();
    this.loadCurrencies();
    this.filterCurrency();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private loadCurrencies(): void {
    this.service.get(Api.CURRENCY_FOR_DD).subscribe((res: any[]) => {
      if (res) {
        this.currencyList = res;
        this.filteredCurrency.next(res.slice());
        this.syncCurrencyNames();
      }
    });
  }

  /** Called after currency list loads to restore selection for pre-patched IDs (edit mode).
   *  Uses setTimeout so the *ngFor options are rendered before setValue fires. */
  syncCurrencyNames(): void {
    setTimeout(() => {
      const custId = this.customerCurrencyId?.value;
      if (custId) {
        const curr = this.currencyList.find(c => c.id === custId);
        if (curr) {
          this.customerCurrencyId.setValue(custId, { emitEvent: false });
          this.customerCurrencyName.setValue(curr.name, { emitEvent: false });
        }
      }
      const vendId = this.vendorCurrencyId?.value;
      if (vendId) {
        const curr = this.currencyList.find(c => c.id === vendId);
        if (curr) {
          this.vendorCurrencyId.setValue(vendId, { emitEvent: false });
          this.vendorCurrencyName.setValue(curr.name, { emitEvent: false });
        }
      }
    });
  }

  filterCurrency(): void {
    this.currencySearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      map(search => {
        if (!this.currencyList) { return []; }
        if (!search) { return this.currencyList.slice(); }
        return this.currencyList.filter(c => c.name.toLowerCase().indexOf(search.toLowerCase()) > -1);
      }),
      delay(200)
    ).subscribe(filtered => {
      this.searching = false;
      this.filteredCurrency.next(filtered);
    }, () => { this.searching = false; });
  }

  onCustomerCurrencySelect(currencyId: number): void {
    const curr = this.currencyList.find(c => c.id === currencyId);
    this.customerCurrencyName.setValue(curr ? curr.name : null);
  }

  onVendorCurrencySelect(currencyId: number): void {
    const curr = this.currencyList.find(c => c.id === currencyId);
    this.vendorCurrencyName.setValue(curr ? curr.name : null);
  }

  ngAfterViewInit() {
    if (this.creditTermForm.get('customerFlag').value) {
      this.showCustomerFields = true;
    }
    if (this.creditTermForm.get('vendorFlag').value) {
      this.showVendorFields = true;
    }
  }

  ngAfterViewChecked() {
    this.changeDetectorRef.detectChanges();
  }

  setCustomerFlagChangeEvent() {
    this.customerFlag.valueChanges.subscribe(checked => {
      if (checked) {
        this.showCustomerFields = true;
        this.setCustomerValidators();
      } else {
        this.showCustomerFields = false;
        this.clearCustomerValidators();
        this.resetCustomerValues();
      }
      this.validateAtLeastOne();
    });
  }

  setVendorFlagChangeEvent() {
    this.vendorFlag.valueChanges.subscribe(checked => {
      if (checked) {
        this.showVendorFields = true;
        this.setVendorValidators();
      } else {
        this.showVendorFields = false;
        this.clearVendorValidators();
        this.resetVendorValues();
      }
      this.validateAtLeastOne();
    });
  }

  private validateAtLeastOne() {
    const customerChecked = !!this.customerFlag.value;
    const vendorChecked = !!this.vendorFlag.value;
    if (!customerChecked && !vendorChecked) {
      this.customerFlag.setErrors({ mustRequired: true });
      this.vendorFlag.setErrors({ mustRequired: true });
    } else {
      this.clearFlagError(this.customerFlag);
      this.clearFlagError(this.vendorFlag);
    }
  }

  private clearFlagError(control: any) {
    if (control.hasError('mustRequired')) {
      const errors = { ...(control.errors || {}) };
      delete errors['mustRequired'];
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
  }

  setCustomerValidators() {
    this.customerCreditDays.setValidators([Validators.required, Validators.pattern('^[1-9][0-9]*$')]);
    this.customerCreditAmount.setValidators([Validators.required, Validators.pattern('^(?:[1-9][0-9]*(?:\\.[0-9]{1,2})?|0\\.(?:0[1-9]|[1-9][0-9]?))$')]);
    this.customerCurrencyId.setValidators([Validators.required]);
    this.customerCreditDays.updateValueAndValidity();
    this.customerCreditAmount.updateValueAndValidity();
    this.customerCurrencyId.updateValueAndValidity();
  }

  clearCustomerValidators() {
    this.customerCreditDays.clearValidators();
    this.customerCreditAmount.clearValidators();
    this.customerCurrencyId.clearValidators();
    this.customerCreditDays.updateValueAndValidity();
    this.customerCreditAmount.updateValueAndValidity();
    this.customerCurrencyId.updateValueAndValidity();
  }

  setVendorValidators() {
    this.vendorCreditDays.setValidators([Validators.required, Validators.pattern('^[1-9][0-9]*$')]);
    this.vendorCreditAmount.setValidators([Validators.required, Validators.pattern('^(?:[1-9][0-9]*(?:\\.[0-9]{1,2})?|0\\.(?:0[1-9]|[1-9][0-9]?))$')]);
    this.vendorCurrencyId.setValidators([Validators.required]);
    this.vendorCreditDays.updateValueAndValidity();
    this.vendorCreditAmount.updateValueAndValidity();
    this.vendorCurrencyId.updateValueAndValidity();
  }

  clearVendorValidators() {
    this.vendorCreditDays.clearValidators();
    this.vendorCreditAmount.clearValidators();
    this.vendorCurrencyId.clearValidators();
    this.vendorCreditDays.updateValueAndValidity();
    this.vendorCreditAmount.updateValueAndValidity();
    this.vendorCurrencyId.updateValueAndValidity();
  }

  resetCustomerValues() {
    this.customerCreditDays.reset();
    this.customerCreditAmount.reset();
    this.customerCategory.reset();
    this.customerCurrencyId.reset();
    this.customerCurrencyName.reset();
    this.customerApprovalStatus.reset();//
  }

  resetVendorValues() {
    this.vendorCreditDays.reset();
    this.vendorCreditAmount.reset();
    this.vendorCategory.reset();
    this.vendorCurrencyId.reset();
    this.vendorCurrencyName.reset();
    this.vendorApprovalStatus.reset();//
  }

  getEntityLevelList() {
    this.service.get(Api.ENTITY_LEVEL_LIST).subscribe(res => {
      this.entityLevelList = res;
    });
  }

  sanitizeIntegerInput(event: any, control: any) {
    const val = event.target.value.replace(/[^0-9]/g, '');
    event.target.value = val;
    control.setValue(val, { emitEvent: false });
    control.updateValueAndValidity();
  }

  sanitizeCurrencyInput(event: any, control: any) {
    let val: string = event.target.value.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) { val = parts[0] + '.' + parts.slice(1).join(''); }
    if (parts[1]?.length > 2) { val = parts[0] + '.' + parts[1].substring(0, 2); }
    event.target.value = val;
    control.setValue(val, { emitEvent: false });
    control.updateValueAndValidity();
  }

  get customerFlag()         { return this.creditTermForm.get('customerFlag'); }
  get customerApprovalStatus(){ return this.creditTermForm.get('customerApprovalStatus'); }//
  get customerCreditDays()   { return this.creditTermForm.get('customerCreditDays'); }
  get customerCreditAmount() { return this.creditTermForm.get('customerCreditAmount'); }
  get customerCategory()     { return this.creditTermForm.get('customerCategory'); }
  get vendorFlag()           { return this.creditTermForm.get('vendorFlag'); }
  get vendorApprovalStatus() { return this.creditTermForm.get('vendorApprovalStatus'); }//
  get vendorCreditDays()     { return this.creditTermForm.get('vendorCreditDays'); }
  get vendorCreditAmount()   { return this.creditTermForm.get('vendorCreditAmount'); }
  get vendorCategory()       { return this.creditTermForm.get('vendorCategory'); }
  get customerCurrencyId()   { return this.creditTermForm.get('customerCurrencyId'); }
  get customerCurrencyName() { return this.creditTermForm.get('customerCurrencyName'); }
  get vendorCurrencyId()     { return this.creditTermForm.get('vendorCurrencyId'); }
  get vendorCurrencyName()   { return this.creditTermForm.get('vendorCurrencyName'); }
}
